import React, { useState } from 'react';
import './styles.css';
import Card from '../Card';
import { FetchItemsFn, Item, ListScope, UiColumn, UiColumn2 } from '../ItemList/types';
import type { IGetRowsParams } from "ag-grid-community";
import { exportIdsToBarcodePdf } from '../../Utils/exportIdsToQrPdf';
import axios from "axios";
import { urlToDataUrl } from '@/Utils/exportInventoryResultToPdf';
import logoAirlines from "../Login/logoAirlines.png";
import { ExcelColumn, exportItemsToExcel } from '@/Utils/exportInventoryResultToExcel';
import UserSettings from '../UserSettings/userSettingsOP';
import ProfilePopup from '../ProfilePopup';
import useAppData from '@/store/useAppData';
import ItemListOP from '../ItemList/itemListOP';

const ItemListCardOP = () => {

    const loggedUser = useAppData(state => state.user);
    const setDisplaySettings = useAppData(state => state.setDisplaySettings);
    const displaySettings = useAppData(state => state.displaySettings);

    const dateFormat = (date: string, dateOnly?: boolean) => {
        const d = new Date(date);

        const pad = (n: number) => String(n).padStart(2, "0");

        if (dateOnly) {
            return (
                pad(d.getDate()) + "." +
                pad(d.getMonth() + 1) + "." +
                d.getFullYear()
            );
        } else {
            return (
                pad(d.getDate()) + "." +
                pad(d.getMonth() + 1) + "." +
                d.getFullYear() + " " +
                pad(d.getHours()) + ":" +
                pad(d.getMinutes()) + ":" +
                pad(d.getSeconds())
            );
        }
    };

    const [scopeState, setScopeState] = React.useState<ListScope>("all");
    const [agParamsState, setAgParamsState] = React.useState<IGetRowsParams | null>(null);
    const stocksItemsRef = React.useRef<Item[]>([]);
    const stocksItemsAgParamsRef = React.useRef<any | null>(null);
    const [isPrinting, setIsPrinting] = React.useState(false);
    // const [displaySettings, setDisplaySettings] = React.useState(false);
    const [profileOpen, setProfileOpen] = useState(false);

    const userColumns: UiColumn<Item>[] = [
        { field: "stockname", headerName: "Sklad", flex: 1, filter: "text", floatingFilter: true },
        { field: "username", headerName: "Uživatel", flex: 2, filter: "text", floatingFilter: true },
        {
            field: "issueddate",
            headerName: "Vydáno",
            flex: 1,
            filter: 'text',
            floatingFilter: true,
            valueFormatter: params => {
                if (!params.value) return "";

                return dateFormat(params.value, true);
            }
        }
    ];

    const columns: UiColumn<Item>[] = [
        { field: "id", headerName: "Id", flex: 1, filter: "number", floatingFilter: true },
        { field: "name", headerName: "Název", flex: 3, filter: "text", floatingFilter: true },
        { field: "state", headerName: "Stav", flex: 1, filter: "text", floatingFilter: true },
        { field: "list1", headerName: "Kolekce", flex: 1, filter: "text", floatingFilter: true },
        { field: "list2", headerName: "Pohlaví", flex: 1, filter: "text", floatingFilter: true },
        { field: "list3", headerName: "Konfekční velikost", flex: 1, filter: "text", floatingFilter: true },
        { field: "quantity", headerName: "Počet", flex: 1, filter: "number", floatingFilter: true, editable: scopeState === "allItems" && loggedUser?.userrole === "admin" },
        ...(scopeState === "users" ? userColumns : [])
    ];

    const columnsExcel = [
        { header: "Id", field: "id", width: 10, type: "text" },
        { header: "Název", field: "name", width: 35, type: "text" },
        { header: "Stav", field: "state", width: 12, type: "text" },
        { header: "Kolekce", field: "list1", width: 14, type: "text" },
        { header: "Pohlaví", field: "list2", width: 12, type: "text" },
        { header: "Velikost", field: "list3", width: 12, type: "text" },
        { header: "Počet", field: "quantity", width: 10, type: "number", align: "right" },
    ] satisfies readonly ExcelColumn<Item>[];

    const columnsExceIssuedByUser = [
        { header: "Id", field: "id", width: 10, type: "text" },
        { header: "Název", field: "name", width: 35, type: "text" },
        { header: "Stav", field: "state", width: 12, type: "text" },
        { header: "Kolekce", field: "list1", width: 14, type: "text" },
        { header: "Pohlaví", field: "list2", width: 12, type: "text" },
        { header: "Velikost", field: "list3", width: 12, type: "text" },
        { header: "Počet", field: "quantity", width: 10, type: "number", align: "right" },
        { header: "Sklad", field: "stockname", width: 12, type: "text" },
        { header: "Zaměstnanec", field: "username", width: 12, type: "text" },
    ] satisfies readonly ExcelColumn<Item>[];

    const fetchItems: FetchItemsFn<Item> = async ({ startRow, endRow, agParams }) => {
        if (scopeState === "all") {
            const { rows, totalCount, countsByState, totalQuantity } = await getStockItems(startRow, endRow, { ...agParams, withoutQuantity: true });
            stocksItemsRef.current = rows;
            stocksItemsAgParamsRef.current = agParams;
            return { rows, totalCount, countsByState, totalQuantity };
        } else if (scopeState === "allItems") {
            const { rows, totalCount, countsByState, totalQuantity } = await getStockItems(startRow, endRow, agParams);
            stocksItemsRef.current = rows;
            stocksItemsAgParamsRef.current = agParams;
            return { rows, totalCount, countsByState, totalQuantity };
        } else if (scopeState === "users") {
            const { rows, totalCount, countsByState, totalQuantity } = await getIssuedItemsByUser(startRow, endRow, agParams);
            stocksItemsRef.current = rows;
            stocksItemsAgParamsRef.current = agParams;
            return { rows, totalCount, countsByState, totalQuantity };
        }

        stocksItemsRef.current = [];
        return { rows: [], totalCount: 0 };
    };

    const fetchItemsPrintCode = async () => {
        if (scopeState === "all") {
            const { rows } = await getStockItems(0, 100000, { ...stocksItemsAgParamsRef.current, withoutQuantity: true });

            return rows;
        } else if (scopeState === "allItems") {
            const { rows } = await getStockItems(0, 100000, stocksItemsAgParamsRef.current);

            return rows;
        } else if (scopeState === "users") {
            const { rows } = await getIssuedItemsByUser(0, 100000, stocksItemsAgParamsRef.current);
            return rows;
        }
    };

    const getStockItems = async (startRow, endRow, agParams) => {
        try {
            console.log(agParams);
            const res = await (
                axios({
                    method: "post",
                    url: "api/stockItem/getAll",
                    data: {
                        start: startRow,
                        end: endRow,
                        stockId: agParams.stockId,
                        filterModel: agParams.filterModel,
                        sortModel: agParams.sortModel,
                        withoutQuantity: agParams?.withoutQuantity
                    }
                })
            );
            console.log("CountsByState:");
            console.log(res.data?.countsByState);
            return {
                rows: res.data.stocksItems,
                totalCount: res.data.totalCount,
                countsByState: res.data?.countsByState,
                totalQuantity: res.data?.totalQuantity
            };
        } catch (err) {
            console.log(err);
            throw err;
        }
    }

    const getIssuedItemsByUser = async (startRow, endRow, agParams) => {
        try {
            console.log(agParams);
            const res = await (
                axios({
                    method: "post",
                    url: "api/issuedItem/getByUser",
                    data: {
                        start: startRow,
                        end: endRow,
                        userId: agParams.selectedUser,
                        stockId: agParams.stockId,
                        filterModel: agParams.filterModel,
                        sortModel: agParams.sortModel
                    }
                })
            );

            return scopeState === "all" ?
                {
                    rows: res.data.stocksItems,
                    totalCount: res.data.totalCount,
                    countsByState: res.data?.countsByState,
                    totalQuantity: res.data?.totalQuantity
                }
                :
                {
                    rows: res.data.issuedItems,
                    totalCount: res.data.totalCount,
                    countsByState: res.data?.countsByState,
                    totalQuantity: res.data?.totalQuantity
                }
        } catch (err) {
            console.log(err);
            throw err;
        }
    }

    const onChangedScope = (scope: ListScope) => {
        setScopeState(scope);
    };

    function stripDiacritics(text: string) {
        return text
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");
    }

    const onPrintQRCodes = async (selectedItems: Item[]) => {

        setIsPrinting(true);

        try {
            // Let React / browser repaint first
            await new Promise(resolve => setTimeout(resolve, 0));

            let rows = [];
            if (selectedItems && selectedItems.length > 0) {
                rows = selectedItems;
            } else {
                rows = await fetchItemsPrintCode();
            }

            if (!rows || rows.length === 0) {
                console.warn("Žádné položky k tisku QR kódů.");
                return;
            }
            exportIdsToBarcodePdf(
                rows.map(item => String(item.id)),
                rows.map(item => stripDiacritics(`${item.id},${item.name},${item.state},${item.list1},${item.list2},${item.list3}`)),
                "SkladovePolozkyBarcodes.pdf",
                {
                    columns: 3,
                    barcodeWidthMm: 50,
                    barcodeHeightMm: 20
                }
            );
        } finally {
            setIsPrinting(false);
        }
    };

    const onPrintStockItems = async () => {

        setIsPrinting(true);

        try {
            const rows = await fetchItemsPrintCode();

            if (!rows || rows.length === 0) {
                console.warn("Žádné položky k tisku.");
                return;
            }

            const logoBase64 = await urlToDataUrl(logoAirlines);

            const title = scopeState === "all" ? "Skladové zásoby" : scopeState === "allItems" ? "Skladové položky" : "Vydané na zaměstanance";

            const selectedColumnsExcel = scopeState === "users" ? columnsExceIssuedByUser : columnsExcel;

            await exportItemsToExcel<Item>(rows, selectedColumnsExcel, {
                title: title,
                fileName: `${title}.xlsx`,
                sheetName: title,
                logoBase64,
                logoExt: "png",
                userName: "Admin"
            });
        } finally {
            setIsPrinting(false);
        }
    };

    const onClickSettings = () => {
        setDisplaySettings(true);
    };

    return <>
        {displaySettings && (
            <div className="inv-modal-overlay1" onMouseDown={() => setDisplaySettings(false)}>
                <div
                    className="inv-modal1"
                    onMouseDown={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                >
                    <UserSettings></UserSettings>
                </div>
            </div>
        )}
        {profileOpen && (
            <ProfilePopup onClose={() => setProfileOpen(false)} />
        )}
        {!displaySettings && <Card
            title="Seznam položek"
            onSettingsClick={onClickSettings}
            onProfileClick={() => setProfileOpen(true)}
        >
            {isPrinting && <div className="qr-caption">Generuji QR kódy…</div>}
            <ItemListOP
                columns={columns}
                fetchItems={fetchItems}
                onChangedScope={onChangedScope}
                scope={scopeState}
                onPrintQRCodes={onPrintQRCodes}
                onPrintStockItems={onPrintStockItems}
                rowSelectionMode={scopeState != "users" ? true : false}
            />
        </Card>}</>;
};

export default ItemListCardOP;